import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useAudioPlayer } from 'pages/TranscriptEditorPage/AudioPlayerContext';
import {
  actionsSelector,
  activeWordIdSelector,
  scrollToWordIdSelector,
  showDeletedWordsSelector,
} from './selectors';
import { EditorUiState, EditorUiStore } from './types';

const defaultState: EditorUiState = {
  activeWordId: undefined,
  isScrolling: false,
  selectedWordIds: [],
  showDeletedWords: true,
  scrollingSegmentWordId: undefined,
  scrollRequest: undefined,
  wordSyncDisabled: false,
};

const useEditorUiStore = create<EditorUiStore>()((set, get) => ({
  ...defaultState,
  actions: {
    activateWord: (activeWordId, context = 'default') => {
      const scrollRequest = !activeWordId
        ? undefined
        : {
            wordId: activeWordId,
            context,
          };

      set((s) => {
        if (s.activeWordId === activeWordId) {
          return {};
        }

        return {
          activeWordId,
          // don't set scrollToWord if scrolling is currently in progress.  Scrolling
          // gets real janky if we try scrolling an element into view when a scrolling
          // operation is already in progr0ess
          scrollRequest: s.isScrolling ? s.scrollRequest : scrollRequest,
        };
      });
    },
    toggleDeletedWordsVisibility: (isVisible) => {
      set((s) => ({ showDeletedWords: isVisible ?? !s.showDeletedWords }));
    },
    toggleIsScrolling: (isScrolling) => {
      set((s) => ({
        isScrolling,
        scrollingSegmentWordId: isScrolling
          ? s.scrollingSegmentWordId
          : undefined,
      }));
    },
    reset: () => {
      set(defaultState);
    },
    selectWords: (selectedWordIds) => {
      set({ selectedWordIds });
    },
    scrollToSegment: (wordId) => set({ scrollingSegmentWordId: wordId }),
    toggleWordSync: (enabled) => {
      set({ wordSyncDisabled: !enabled });
    },
  },
}));

export const useEditorUiActions = () => useEditorUiStore(actionsSelector);

export function useEditorUiSubscription(
  cb: (current: EditorUiStore, prev: EditorUiStore) => void,
) {
  useEffect(() => useEditorUiStore.subscribe(cb), [cb]);
}

export const useActiveWordId = () => useEditorUiStore(activeWordIdSelector);

export const useIsWordActive = (wordId: number) =>
  useEditorUiStore(
    useCallback((s) => activeWordIdSelector(s) === wordId, [wordId]),
  );

export const useIsWordScrollTarget = (wordId: number) =>
  useEditorUiStore(
    useCallback((s) => scrollToWordIdSelector(s) === wordId, [wordId]),
  );

export const useIsWordSelected = (wordId: number) =>
  useEditorUiStore(
    useCallback((s) => !!s.selectedWordIds?.includes(wordId), [wordId]),
  );

export const useShowDeletedWords = () =>
  useEditorUiStore(showDeletedWordsSelector);

export const useIsWordVisible = (isDeleted: boolean) =>
  useEditorUiStore(
    useCallback((s) => showDeletedWordsSelector(s) && isDeleted, [isDeleted]),
  );

type Callback = () => void;

export const useWithDisabledWordSync = () => {
  const { toggleWordSync } = useEditorUiActions();
  const { player } = useAudioPlayer();
  const fnRef = useRef<Callback>();
  const fnCompletedRef = useRef(false);

  const handleSeeked = useCallback(() => {
    if (fnCompletedRef.current) {
      toggleWordSync(true);
      player.off('seeked', handleSeeked);
    }
  }, [player, toggleWordSync]);

  useEffect(() => handleSeeked, [handleSeeked]);

  return useCallback(
    (fn: () => void) => {
      fnRef.current = fn;
      fnCompletedRef.current = false;

      toggleWordSync(false);
      player.on('seeked', handleSeeked);
      fn();
      fnCompletedRef.current = true;
    },
    [handleSeeked, player, toggleWordSync],
  );
};
